import { useEffect, useCallback, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { UseTenantStatus, AutomationStatus } from '../types/tenant';
import logger from '../utils/logger';

// Interface for Socket.IO status updates
interface TenantStatusUpdate {
  tenantId: string;
  tenantName?: string;
  status: 'IN_PROGRESS' | 'SUCCESS' | 'FAILURE' | 'IDLE';
  message?: string;
  process?: string;
  timestamp?: Date | string;
}

// Validation function for incoming status updates
const isValidStatusUpdate = (data: unknown): data is TenantStatusUpdate => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const update = data as Record<string, unknown>;

  // Required fields
  if (typeof update.tenantId !== 'string' || update.tenantId.length === 0) {
    return false;
  }

  const validStatuses = ['IN_PROGRESS', 'SUCCESS', 'FAILURE', 'IDLE'];
  if (
    typeof update.status !== 'string' ||
    !validStatuses.includes(update.status)
  ) {
    return false;
  }

  // Optional fields validation
  if (
    update.tenantName !== undefined &&
    typeof update.tenantName !== 'string'
  ) {
    return false;
  }

  if (update.message !== undefined && typeof update.message !== 'string') {
    return false;
  }

  if (update.process !== undefined && typeof update.process !== 'string') {
    return false;
  }

  return true;
};

const calculateOverallStatus = (
  clientAutomation: AutomationStatus,
  dbAutomation: AutomationStatus,
  currentStatus: 'IDLE' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILURE'
): 'IDLE' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILURE' => {
  const hasActiveAutomation =
    (clientAutomation?.status && clientAutomation.status !== 'IDLE') ||
    (dbAutomation?.status && dbAutomation.status !== 'IDLE');

  if (!hasActiveAutomation) return currentStatus;

  if (
    clientAutomation.status === 'FAILURE' ||
    dbAutomation.status === 'FAILURE'
  )
    return 'FAILURE';
  if (
    clientAutomation.status === 'SUCCESS' &&
    dbAutomation.status === 'SUCCESS'
  )
    return 'SUCCESS';
  if (
    clientAutomation.status === 'IN_PROGRESS' ||
    dbAutomation.status === 'IN_PROGRESS'
  )
    return 'IN_PROGRESS';

  return currentStatus;
};

interface SocketTenantStatusOptions {
  tenantStatus: UseTenantStatus;
  setTenantStatus: React.Dispatch<React.SetStateAction<UseTenantStatus>>;
  socketUrl?: string;
  token?: string;
  enabled?: boolean;
}

export const useSocketTenantStatus = ({
  tenantStatus: _tenantStatus,
  setTenantStatus,
  socketUrl,
  token,
  enabled = true,
}: SocketTenantStatusOptions) => {
  const socketRef = useRef<Socket | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);

  // Latest-ref pattern to avoid stale closures in socket event listeners
  const handleTenantStatusUpdate = useCallback(
    (data: unknown) => {
      // Validate incoming data
      if (!isValidStatusUpdate(data)) {
        logger.error('Invalid status update received', { data });
        return;
      }

      const statusUpdate = data as TenantStatusUpdate;

      setTenantStatus((prev) => {
        // For tenant creation, accept ALL updates when we're in progress
        // or if it's the first update after submission
        const isRelevantUpdate =
          prev.status === 'IDLE' || // Accept any update when starting
          statusUpdate.tenantId === prev.createdTenantId ||
          statusUpdate.tenantId === prev.tempTenantId ||
          statusUpdate.tenantName === prev.createdTenantName ||
          (prev.status === 'IN_PROGRESS' && !prev.createdTenantId); // Accept updates while waiting for tenant ID

        if (!isRelevantUpdate) {
          return prev;
        }

        const prevMessages = prev.detailedMessages || [];
        const timestamp = new Date().toLocaleTimeString();
        const messageText =
          statusUpdate.message || `Status: ${statusUpdate.status}`;
        const newMessage = `[${timestamp}] ${messageText}`;

        // Rate limiting: prevent too many messages in short time
        const now = Date.now();
        const lastMessageTime = prev.lastMessageTime || 0;
        const MESSAGE_RATE_LIMIT_MS = 100; // Max 1 message per 100ms

        if (
          now - lastMessageTime < MESSAGE_RATE_LIMIT_MS &&
          prevMessages.length > 0
        ) {
          // Skip this message if we're receiving too fast
          return prev;
        }

        // Bounded queue with strict limit enforcement
        const MAX_MESSAGES = 10;
        let newDetailedMessages: string[];

        if (prevMessages.length >= MAX_MESSAGES) {
          // Remove oldest messages to make room, keep array bounded
          newDetailedMessages = [
            ...prevMessages.slice(-(MAX_MESSAGES - 1)),
            newMessage,
          ];
        } else {
          newDetailedMessages = [...prevMessages, newMessage];
        }

        // Handle automation type based on process field
        let updatedClientAutomation = prev.clientAutomation || {
          status: 'IDLE',
        };
        let updatedDbAutomation = prev.dbAutomation || { status: 'IDLE' };

        const automationUpdate = {
          status: statusUpdate.status,
          message: statusUpdate.message,
          lastUpdated: timestamp,
        };

        if (statusUpdate.process === 'tenantClientAutomation') {
          updatedClientAutomation = automationUpdate;
        } else if (statusUpdate.process === 'tenantDbAutomation') {
          updatedDbAutomation = automationUpdate;
        }

        const overallStatus: 'IDLE' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILURE' = statusUpdate.process
          ? calculateOverallStatus(
              updatedClientAutomation,
              updatedDbAutomation,
              prev.status
            )
          : statusUpdate.status;

        const updatedStatus: UseTenantStatus = {
          ...prev,
          status: overallStatus,
          message: messageText,
          detailedMessages: newDetailedMessages,
          lastMessageTime: now,
          automationStarted:
            statusUpdate.status !== 'IDLE' || prev.automationStarted,
          clientAutomation: updatedClientAutomation,
          dbAutomation: updatedDbAutomation,
        };

        // Track temp ID for IN_PROGRESS updates
        if (statusUpdate.tenantId?.startsWith('temp-') && !prev.tempTenantId) {
          updatedStatus.tempTenantId = statusUpdate.tenantId;
        }

        // Set tenant name if not already set
        if (statusUpdate.tenantName && !prev.createdTenantName) {
          updatedStatus.createdTenantName = statusUpdate.tenantName;
        }

        return updatedStatus;
      });
    },
    [setTenantStatus]
  );

  // Keep ref updated with latest callback
  const handleTenantStatusUpdateRef = useRef(handleTenantStatusUpdate);
  useEffect(() => {
    handleTenantStatusUpdateRef.current = handleTenantStatusUpdate;
  }, [handleTenantStatusUpdate]);

  useEffect(() => {
    if (!enabled || !socketUrl || !token) {
      // Clean up existing socket when disabled
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocketConnected(false);
      return;
    }

    // Validate socketUrl format
    try {
      new URL(socketUrl);
    } catch (error) {
      logger.error('Invalid socketUrl provided', {
        socketUrl,
        error: error instanceof Error ? error.message : String(error),
      });
      setSocketConnected(false);
      return;
    }

    // Clean up any existing socket before creating new one
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socket = io(socketUrl, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'], // WebSocket first, polling fallback
      autoConnect: true,
      auth: {
        token: token,
      },
      timeout: 20000, // 20 second timeout for connection
      forceNew: true, // Force new connection to avoid session issues
      upgrade: true, // Allow transport upgrades from polling to WebSocket
      rememberUpgrade: true, // Remember successful WebSocket upgrades
      reconnection: true, // Enable reconnection
      reconnectionDelay: 1000, // Initial delay before reconnection
      reconnectionDelayMax: 5000, // Maximum delay between reconnections
      reconnectionAttempts: 5, // Number of reconnection attempts
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      const transportName = socket.io.engine?.transport?.name || 'unknown';
      logger.info('Socket connected', { transportName });
      setSocketConnected(true);

      // Authenticate the socket connection
      socket.emit('authenticate', token);
    });

    socket.on('authenticated', () => {
      // Send a test ping to verify connection
      socket.emit('ping', { timestamp: Date.now() }, () => {});
    });

    socket.on('unauthorized', (error: unknown) => {
      logger.warn('Socket authentication rejected', { error: String(error) });
    });

    // Listen for transport upgrades
    const handleUpgrade = (transport: any) => {
      logger.info('Transport upgraded', { transportName: transport?.name || 'unknown' });
    };
    const handleUpgradeError = (error: any) => {
      logger.warn('Transport upgrade failed', { error: error?.message || String(error) });
    };

    // Listen for transport upgrades (engine.io events)
    (socket.io as any).on('upgrade', handleUpgrade);
    (socket.io as any).on('upgradeError', handleUpgradeError);

    // Listen for tenant status updates
    socket.on('tenant-status-update', (statusUpdate) => {
      handleTenantStatusUpdateRef.current(statusUpdate);
    });

    socket.on('disconnect', (reason) => {
      setSocketConnected(false);
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, reconnect manually
        socket.connect();
      }
    });

    socket.on('connect_error', (error) => {
      logger.error('Socket connection failed', { error: error.message });

      // If WebSocket fails, force polling for next attempt
      if (
        error.message.includes('websocket') ||
        error.message.includes('WebSocket')
      ) {
        logger.info('WebSocket failed, switching to polling only');
        socket.io.opts.transports = ['polling'];
      }
    });

    socket.on('reconnect', (attemptNumber) => {
      const transportName = socket.io.engine?.transport?.name || 'unknown';
      logger.info('Socket reconnected', {
        attemptNumber,
        transportName,
      });

      // Reset transports after successful reconnection to allow WebSocket retry
      socket.io.opts.transports = ['websocket', 'polling'];
    });

    socket.on('reconnect_error', (error: Error) => {
      logger.warn('Socket reconnection failed', { error: error.message });
    });

    // Cleanup on unmount or when dependencies change
    return () => {
      if (socketRef.current) {
        (socketRef.current.io as any).off('upgrade', handleUpgrade);
        (socketRef.current.io as any).off('upgradeError', handleUpgradeError);
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocketConnected(false);
    };
  }, [enabled, socketUrl, token]);

  return {
    socketConnected,
  };
};
